/** Named, interactable settlement sites derived from W2 geometry + W3 history. */
import * as pmath from "../pmath.js";
import type { PlanetSampler } from "../universe.js";
import { LEVEL_REGION, dirToCell, packCell, type CellId } from "./cells.js";
import { type CityGraph, USE_MARKET_SQ, dirToLocal, localToDir, regionFrame } from "./citygraph.js";
import { edgeNeighbor, SIDE_NEG_U, SIDE_NEG_V, SIDE_POS_U, SIDE_POS_V } from "./boundary.js";
import { collapseRegion, type RegionChronicle, type SettlementChronicle } from "./collapse.js";
import { settlementSeeds } from "./fields.js";
import { PARCEL_SEED_RADIUS_MULT, TIER_PARAMS, sprawlSeeds } from "./growth.js";
import { settlementName } from "./settlementNames.js";
import {civicApproach} from './civics.js';

export const SETTLEMENT_TIER_NAMES = ["WAYSTATION", "HAMLET", "VILLAGE", "TOWN", "CITY", "METROPOLIS"] as const;

export function settlementTierName(tier: number): string {
  return SETTLEMENT_TIER_NAMES[tier] ?? "SETTLEMENT";
}

export interface ParsedRegionSettlementId {
  planetSeed: string;
  regionId: CellId;
  settlementK: number;
}

export function parseRegionSettlementId(id: string): ParsedRegionSettlementId | null {
  const match = /^(.*):stl:([0-5]):(\d+):(\d+):(\d+)$/.exec(id);
  if (!match || match[1].length === 0) return null;
  const face = Number(match[2]), x = Number(match[3]), y = Number(match[4]), settlementK = Number(match[5]);
  if (![face, x, y, settlementK].every(Number.isSafeInteger)) return null;
  return { planetSeed: match[1], regionId: { face, x, y, level: LEVEL_REGION }, settlementK };
}

export interface RegionSettlement {
  id: string;
  name: string;
  tier: number;
  population: number;
  seedK: number;
  regionId: CellId;
  /** Region-chart settlement centre. */
  u: number;
  v: number;
  /** Public ward approach for arrivals; u/v still identify the ward itself. */
  arrival?: {u:number;v:number};
  /** Region-chart board kiosk; null below village tier or without an open square. */
  board: { u: number; v: number } | null;
  /**
   * How many parcels the GROWER actually built for this site. Every dealt seed
   * becomes a named settlement here — history, name and population are analytic
   * (derived from the seed's tier, not from what got built) — but the grower is
   * free to build NOTHING on it: a steep site, a d_sep merge into a neighbour, or
   * an exhausted parcel budget all leave a seed with 0 parcels. Those are GHOSTS:
   * a name and a population over empty ground (~23% of dealt sites planet-wide).
   * Anything that points a player at a place — map pins, scan markers, quest
   * targets — must require `parcels > 0`.
   */
  parcels: number;
}

export interface SettlementInfluence {
  regionId: CellId;
  seedK: number;
  tier: number;
  /** Chord distance from the query to the settlement centre, metres. */
  dist: number;
  /** Conservative grown parcel radius used by scatter/resource exclusions. */
  radius: number;
}

/**
 * Analytic whole-planet settlement footprint query. It needs no CityGraph and is
 * therefore safe for resource generation and far scatter before region residency.
 * The current region plus its eight topological neighbours covers the largest
 * grown tier radius and metro-sprawl pseudo-seeds.
 */
export function settlementInfluenceAt(
  sampler: PlanetSampler, dx: number, dy: number, dz: number, slackM = 0,
): SettlementInfluence | null {
  const dl = pmath.hypot(dx, dy, dz);
  if (dl < 1e-9) return null;
  dx /= dl; dy /= dl; dz /= dl;
  const R = sampler.def.radius;
  const root = dirToCell(R, LEVEL_REGION, dx, dy, dz);
  const negU = edgeNeighbor(R, root, SIDE_NEG_U), posU = edgeNeighbor(R, root, SIDE_POS_U);
  const negV = edgeNeighbor(R, root, SIDE_NEG_V), posV = edgeNeighbor(R, root, SIDE_POS_V);
  const cells = [
    root, negU, posU, negV, posV,
    edgeNeighbor(R, negU, SIDE_NEG_V), edgeNeighbor(R, negU, SIDE_POS_V),
    edgeNeighbor(R, posU, SIDE_NEG_V), edgeNeighbor(R, posU, SIDE_POS_V),
  ];
  const seen = new Set<number>();
  const dir = new Float64Array(3);
  let best: SettlementInfluence | null = null;
  const consider = (cell: CellId, seedK: number, tier: number, sx: number, sy: number, sz: number): void => {
    const dot = Math.max(-1, Math.min(1, dx * sx + dy * sy + dz * sz));
    const dist = Math.sqrt(Math.max(0, 2 - 2 * dot)) * R;
    const radius = PARCEL_SEED_RADIUS_MULT * (TIER_PARAMS[tier]?.radiusM ?? 30);
    if (dist > radius + slackM) return;
    if (!best || dist - radius < best.dist - best.radius) best = {
      regionId: { ...cell }, seedK, tier, dist, radius,
    };
  };
  for (const cell of cells) {
    const key = packCell(cell);
    if (seen.has(key)) continue;
    seen.add(key);
    const raw = settlementSeeds(sampler, cell);
    for (const seed of raw) consider(cell, seed.k, seed.tier, seed.dx, seed.dy, seed.dz);
    const frame = regionFrame(R, cell);
    for (const seed of sprawlSeeds(sampler, cell, frame, raw)) {
      localToDir(frame, seed.u, seed.v, dir);
      consider(cell, seed.k, seed.tier, dir[0], dir[1], dir[2]);
    }
  }
  return best;
}

function parcelCentroid(graph: CityGraph, parcel: number): [number, number] {
  const poly = graph.parcels[parcel].poly;
  let u = 0, v = 0;
  const count = poly.length / 2;
  for (let i = 0; i < poly.length; i += 2) { u += poly[i]; v += poly[i + 1]; }
  return count > 0 ? [u / count, v / count] : [0, 0];
}

function parcelRefs(settlement: SettlementChronicle): number[] {
  const refs = new Set<number>();
  for (const entry of settlement.entries) if (entry.kind === "district-founded") {
    for (const ref of entry.refs) refs.add(ref);
  }
  return [...refs].sort((a, b) => a - b);
}

/** All named sites in an already-materialized region, in stable seed order. */
export function regionSettlements(
  sampler: PlanetSampler,
  graph: CityGraph,
  chronicle: RegionChronicle = collapseRegion(sampler, graph),
): RegionSettlement[] {
  const raw = settlementSeeds(sampler, graph.regionId);
  const uv: [number, number] = [0, 0];
  const centres = new Map<number, [number, number]>();
  for (const seed of raw) {
    dirToLocal(graph.frame, seed.dx, seed.dy, seed.dz, uv);
    centres.set(seed.k, [uv[0], uv[1]]);
  }
  for (const seed of sprawlSeeds(sampler, graph.regionId, graph.frame, raw)) centres.set(seed.k, [seed.u, seed.v]);
  // The ward is the visible and authoritative town centre at every size.
  for(const site of graph.civics??[])centres.set(site.seedK,[site.ward.u,site.ward.v]);

  return chronicle.settlements.map((settlement) => {
    const refs = parcelRefs(settlement);
    const civic = graph.civics?.find(site => site.seedK === settlement.seedK);
    let centre = centres.get(settlement.seedK);
    if (!centre && refs.length > 0) {
      let u = 0, v = 0;
      for (const ref of refs) { const p = parcelCentroid(graph, ref); u += p[0]; v += p[1]; }
      centre = [u / refs.length, v / refs.length];
    }
    centre ??= [0, 0];
    let board: { u: number; v: number } | null = null;
    if (settlement.tier >= 2) {
      const squares = refs.filter((ref) => graph.parcels[ref]?.use === USE_MARKET_SQ);
      let best = -1, bestD = Infinity;
      for (const ref of squares) {
        const [u, v] = parcelCentroid(graph, ref);
        const du = u - centre[0], dv = v - centre[1];
        const d = du * du + dv * dv;
        if (d < bestD || (d === bestD && ref < best)) { best = ref; bestD = d; }
      }
      if (best >= 0) { const [u, v] = parcelCentroid(graph, best); board = { u, v }; }
    }
    return {
      id: settlement.settlementId,
      name: settlementName(settlement.settlementId),
      tier: settlement.tier,
      population: settlement.populationNow,
      seedK: settlement.seedK,
      regionId: { ...graph.regionId },
      u: centre[0], v: centre[1], board,
      ...(civic ? {arrival:civicApproach(civic,'ward')} : {}),
      parcels: refs.length,
    };
  });
}
